import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { apiCache } from '../utils/cache';
import { dataCache, CACHE_DURATIONS } from '../utils/dataCache';
import { useLanguage } from '../contexts/LanguageContext';
import { languageContent } from '../utils/languageContent';
import { getSuperAdminStatus } from '../utils/adminUtils';
import { isCloudflare503Error, isNetworkError } from '../utils/connectionUtils';
import BottomNav from '../components/BottomNav';
import LessonBannerAd from '../components/LessonBannerAd';
import PullToRefresh from '../components/PullToRefresh';
import SyncButton from '../components/SyncButton';
import SkeletonLoader from '../components/SkeletonLoader';

interface CourseLevel {
  id: string;
  level_code: string;
  level_name: string;
  description: string;
  order_index: number;
  chapter_count?: number;
  lesson_count?: number;
  completed_lessons?: number;
  progress_percentage?: number;
}

const LevelsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const content = languageContent[currentLanguage];
  const [levels, setLevels] = useState<CourseLevel[]>([]);
  const [loading, setLoading] = useState(false); // Start false - show cached data immediately
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [completedLessons, setCompletedLessons] = useState<string[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    const initializeData = async () => {
      // Load progress first (localStorage - instant)
      const completed = await loadProgress();
      
      // Load cached data immediately (instant display)
      const superAdmin = await getSuperAdminStatus();
      setIsSuperAdmin(superAdmin);
      
      const cacheKey = `levels_data_${superAdmin ? 'admin' : 'user'}`;
      const cachedData = dataCache.get<CourseLevel[]>(cacheKey) || apiCache.get(cacheKey);
      
      if (cachedData && cachedData.length > 0) {
        // Process cached data with progress immediately
        const processedCached = processLevelsData(cachedData, completed);
        setLevels(processedCached);
        setLoading(false);
      }
      
      // Load fresh data in background (non-blocking)
      setTimeout(() => {
        loadLevels(superAdmin, false);
      }, 50);
    };
    initializeData();
    
    // Refresh progress when screen comes into focus (user might have completed lessons)
    const handleFocus = () => {
      loadProgress().then(completed => {
        // Re-process existing levels with updated progress
        if (levels.length > 0) {
          const updatedLevels = processLevelsData(levels, completed);
          setLevels(updatedLevels);
        }
      });
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const loadProgress = async () => {
    try {
      const progressKey = 'userProgress';
      const existingProgress = localStorage.getItem(progressKey);
      const progress = existingProgress ? JSON.parse(existingProgress) : {
        completedLessons: []
      };
      const completed = progress.completedLessons || [];
      setCompletedLessons(completed);
      return completed;
    } catch (error) {
      console.error('Error loading progress:', error);
      return [];
    }
  };

  // Helper function to process levels data with counts and progress
  const processLevelsData = (levelsData: any[], completedLessons: string[]): CourseLevel[] => {
    console.log('📊 Processing levels data with progress:', {
      levelsCount: levelsData.length,
      completedLessonsCount: completedLessons.length,
      completedLessons: completedLessons.slice(0, 5) // Log first 5 for debugging
    });
    
    return levelsData.map((level: any) => {
      const chapters = level.Chapters || [];
      const chapterCount = chapters.length;
      
      // Count all lessons across chapters
      const allLessons = chapters.flatMap((chapter: any) => chapter.Lessons || []);
      const lessonCount = allLessons.length;
      
      // Calculate completed lessons for this level
      const levelLessonIds = allLessons.map((lesson: any) => lesson.id);
      const completedInLevel = levelLessonIds.filter((id: string) => completedLessons.includes(id)).length;
      const progressPercentage = lessonCount > 0 ? Math.round((completedInLevel / lessonCount) * 100) : 0;
      
      console.log(`📈 Level ${level.level_code || level.id}: ${completedInLevel}/${lessonCount} lessons completed (${progressPercentage}%)`);
      
      return {
        ...level,
        chapter_count: chapterCount,
        lesson_count: lessonCount,
        completed_lessons: completedInLevel,
        progress_percentage: progressPercentage,
      };
    });
  };

  const loadLevels = async (superAdminStatus?: boolean, forceRefresh: boolean = false) => {
    try {
      if (!forceRefresh) {
        setLoading(true);
      }
      
      // Explicitly check superadmin status - default to false if not provided
      const isSuperAdminUser = superAdminStatus !== undefined ? superAdminStatus : (isSuperAdmin || false);
      
      // Cache key should be different for superadmin vs regular users
      const cacheKey = `levels_data_${isSuperAdminUser ? 'admin' : 'user'}`;
      
      // Check cache first (unless forcing refresh)
      if (!forceRefresh) {
        const cachedData = dataCache.get<CourseLevel[]>(cacheKey) || apiCache.get(cacheKey);
        if (cachedData && cachedData.length > 0) {
          // Process cached data with current progress
          const processedCached = processLevelsData(cachedData, completedLessons);
          setLevels(processedCached);
          setLoading(false);
          // Refresh in background (non-blocking)
          setTimeout(() => loadLevels(superAdminStatus, true), 100);
          return;
        }
      }
      
      // Get fresh completed lessons from localStorage
      const progressKey = 'userProgress';
      const existingProgress = localStorage.getItem(progressKey);
      const progress = existingProgress ? JSON.parse(existingProgress) : {
        completedLessons: []
      };
      const currentCompletedLessons = progress.completedLessons || [];

        // Single optimized query to get all data at once
        // Superadmins can see all levels (including unpublished), regular users only published
        let query = supabase
        .from('CourseLevels')
          .select(`
            *,
            Chapters(
              id,
              Lessons(id)
            )
          `);
        
        // Only filter by published status for non-superadmins
        if (!isSuperAdminUser) {
          query = query.eq('is_published', true);
          console.log('🔍 Regular user - filtering for published levels only');
        } else {
          console.log('🔓 SuperAdmin - loading all levels (including drafts)');
        }
        
        console.log('📤 Executing query...');
        const { data, error } = await query.order('order_index');
        console.log('📥 Query completed', { hasError: !!error, hasData: !!data, dataLength: data?.length || 0 });

        if (error) {
          console.error('❌ ERROR LOADING LEVELS:', error);
          console.error('📋 Full Error Object:', JSON.stringify(error, null, 2));
          console.error('🔍 Error Details:', { 
            isSuperAdminUser, 
            table: 'CourseLevels',
            errorMessage: error.message,
            errorDetails: error.details,
            errorHint: error.hint,
            errorCode: error.code
          });
        
        // Check for Cloudflare 503
        if (isCloudflare503Error(error)) {
          console.warn('⚠️ Cloudflare is down (503). Service will be back soon.');
          // Keep existing data if available
          if (levels.length === 0) {
            console.error('⚠️ No levels loaded and Cloudflare is down. Please try again later.');
          }
          setLoading(false);
          return;
        }
        
        // Check for network errors
        if (isNetworkError(error)) {
          console.warn('⚠️ Network error. Check your internet connection.');
          // Keep existing data if available
          if (levels.length === 0) {
            console.error('⚠️ No levels loaded and network error occurred.');
          }
          setLoading(false);
          return;
        }
        
        // If we have existing data, keep it. Otherwise show error.
        if (levels.length === 0) {
          // No existing data - this is a problem
          console.error('⚠️ No levels loaded and error occurred. Check server connection.');
        }
          setLoading(false);
          return;
        }
        
        console.log(`✅ Loaded ${data?.length || 0} levels (SuperAdmin: ${isSuperAdminUser})`);
        if (data && data.length > 0) {
          console.log('📋 Levels found:', data.map(l => ({ 
            id: l.id, 
            code: l.level_code || l.slug, 
            name: l.level_name || l.title_en,
            published: l.is_published 
          })));
        } else {
          console.warn('⚠️ No levels returned. Possible causes:');
          console.warn('   1. No levels with is_published=true in database');
          console.warn('   2. RLS policy blocking access');
          console.warn('   3. Table name mismatch (CourseLevels vs course_levels)');
          console.warn('👉 Check Supabase Table Editor → CourseLevels → Look for is_published=true');
        }
        
      const levelsData = data || [];
      
      // If refresh returns empty and we have existing data, preserve existing data
      if (levelsData.length === 0 && levels.length > 0) {
        console.warn('No levels data returned from refresh, keeping existing data');
        setLoading(false);
        return;
      }

      // Only update if we have data OR this is initial load (to show proper empty state)
      if (levelsData.length === 0 && levels.length === 0) {
        // Initial load with no data - show empty state
        setLevels([]);
        setLoading(false);
        return;
      }

      // Cache in both caches (only if we have data)
      if (levelsData.length > 0) {
        dataCache.set(cacheKey, levelsData, CACHE_DURATIONS.LEVELS);
        apiCache.set(cacheKey, levelsData, 5 * 60 * 1000);
      }

      // Process data efficiently with latest progress
      const levelsWithCounts = processLevelsData(levelsData, currentCompletedLessons);

      // Always update to ensure progress is current (progress changes frequently)
      setLevels(levelsWithCounts);
    } catch (error) {
      console.error('Error loading levels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLevelSelect = (levelId: string) => {
    navigate(`/chapters/${levelId}`);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // First refresh progress from localStorage
      const completed = await loadProgress();
      
      const superAdmin = await getSuperAdminStatus();
      // Don't clear cache - just force refresh to get latest data
      // This way if refresh fails, we still have cached data visible
      await loadLevels(superAdmin, true);
      
      // Also update existing levels with latest progress
      if (levels.length > 0) {
        const updatedLevels = processLevelsData(levels, completed);
        setLevels(updatedLevels);
      }
    } catch (error) {
      console.error('Error refreshing levels:', error);
      // Keep existing data visible on error
    } finally {
      setIsRefreshing(false);
  }
  };

  return (
    <div className="levels-screen">
      <div className="screen-header">
        <button 
          className="header-btn back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="header-center">
          <h1>{content.level}</h1>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <SyncButton 
            onSync={handleRefresh} 
            isSyncing={isRefreshing}
            size="small"
          />
        <button 
          className="header-btn settings-btn"
          onClick={() => navigate('/settings')}
          aria-label="Open settings"
        >
          ⋯
        </button>
        </div>
      </div>

      <LessonBannerAd />

      <PullToRefresh onRefresh={handleRefresh} disabled={isRefreshing}>
      <div className="levels-container">
        {loading && levels.length === 0 ? (
          <div className="no-levels">
            <SkeletonLoader type="card" count={3} />
          </div>
        ) : levels.length === 0 ? (
          <div className="no-levels">
            <p>No levels available yet.</p>
          </div>
        ) : (
          levels.map((level) => (
          <div 
            key={level.id}
            className="level-card"
            onClick={() => handleLevelSelect(level.id)}
          >
            <div className="level-header">
              <div className="level-badge">{level.level_code}</div>
              <div className="level-status">Available</div>
            </div>
            
            <div className="level-content">
              <h3>{level.level_name || 'Loading...'}</h3>
              <p>{level.description || ''}</p>
              
              <div className="level-meta">
                <span className="meta-item">📚 {level.chapter_count ?? 0} chapters</span>
                <span className="meta-item">📝 {level.lesson_count ?? 0} lessons</span>
              </div>
              
              <div className="level-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${level.progress_percentage ?? 0}%` }}></div>
                </div>
                <span className="progress-text">
                  {!level.lesson_count || level.progress_percentage === 0 
                    ? 'Start Learning' 
                    : `${level.completed_lessons ?? 0}/${level.lesson_count} lessons • ${level.progress_percentage}%`}
                </span>
              </div>
            </div>
          </div>
        )))}
      </div>
      </PullToRefresh>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default LevelsScreen;
